import { getPathWithParams } from '@/components/InstantSearch/util/url-search-params';
import {
  type ALL_WEB_TRAFFIC_SEARCH_PARAM_KEYS,
  type WEB_TRAFFIC_ACTIVITY_SORT_KEYS,
  type WebTrafficSearchParamType,
} from '@/router/searchParams/webTrafficSearchParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const webTraffic = {
  path: `${instance.path}/web-traffic`,
  description:
    "Shows web traffic for the company's tagged website. Overall traffic over time, visits broken down by traffic type, industry, and company size, and a list of the most frequently visited companies",
  navigable: true,
  pathParams: {},
  searchParams: {
    audience: {
      type: 'string',
      description: 'An audience ID to filter the channels by',
      required: false,
      requiresLookup: true,
      lookupType: 'audience',
      supportsNegation: false,
    },
  },
} satisfies NavigableRouteType<'audience'>;

export const attribution = {
  path: `${instance.path}/attribution`,
  description:
    'Show statistics about different attribution methods. These statistics are broken down by opportunity type (Pipeline, Closed Won) and can also be broken down by vendor or campaign.',
  navigable: true,
  pathParams: {},
  searchParams: {
    oppType: {
      type: 'enum',
      enumValues: ['pipeline', 'closedWon'],
      description: 'The type of opportunity to filter the attribution by',
      required: false,
      requiresLookup: false,
      default: 'pipeline',
      supportsNegation: false,
    },
    audience: {
      type: 'string',
      description: 'An audience ID to filter the channels by',
      required: false,
      requiresLookup: true,
      lookupType: 'audience',
      supportsNegation: false,
    },
  },
} satisfies NavigableRouteType<'oppType' | 'audience'>;

export const webTrafficBenchmarks = {
  path: `${instance.path}/web-traffic-benchmarks`,
  description:
    'Shows web traffic benchmarks for the company broken down by channels. The benchmarks are based on the company’s industry and size. The benchmarks are compared to traffic from other companies in the same industry to show marketers how they are performing relative to other companies in the same space.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const webTrafficActivity = {
  path: `${instance.path}/web-traffic-activity`,
  description:
    "Shows a list of all companies that have visited the company's website during a specific date range. The list shows the name of the company, region, industry, revenue range (size), audiences it belongs to, number of impressions, number of visits, and a timestamp for the last visit. The list can be filtered and sorted. If the user is asking to see information on companies, this is the route to send them to.",
  examples: [
    'I want to see a list of companies in audiences A, B, and C which belong to the Direct channel in the healthcare sector and had at least 50 impressions',
    'Show me companies in Africa which had revenue of around $200M sorted by visits descending',
    'Can I see all companies which do not belong to "Audience A" but are in my TAM which have more than 10 impressions and between 1 and 5 visits sorted by impressions descending',
  ],
  navigable: true,
  pathParams: {},
  searchParams: {
    'w.audienceIds': {
      type: 'string[]',
      description: 'Audience IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'audience',
      supportsNegation: true,
    },
    'w.channelIds': {
      type: 'string[]',
      description: 'Channel IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'channel',
      supportsNegation: true,
    },
    'w.vendorIds': {
      type: 'string[]',
      description: 'Vendor IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'vendor',
      supportsNegation: true,
    },
    'w.campaignIds': {
      type: 'string[]',
      description: 'Campaign IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'campaign',
      supportsNegation: true,
    },
    'w.adGroupIds': {
      type: 'string[]',
      description: 'Ad Group IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'adGroup',
      supportsNegation: true,
    },
    'w.keywordIds': {
      type: 'string[]',
      description: 'Keyword IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'ad',
      supportsNegation: true,
    },
    'w.pixelIds': {
      type: 'string[]',
      description: 'Pixel IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'pixel',
      supportsNegation: true,
    },
    'w.search': {
      type: 'string',
      description: 'A string to search for in the company name to filter company list',
      required: false,
      requiresLookup: false,
      supportsNegation: false,
    },
    'w.region.id': {
      type: 'string[]',
      description: 'Region IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'region',
      supportsNegation: true,
    },
    'w.sector.id': {
      type: 'string[]',
      description: 'Sector (industry) IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'sector',
      supportsNegation: true,
    },
    'w.revRange.id': {
      type: 'string[]',
      description: 'Revenue range IDs to filter company list',
      required: false,
      requiresLookup: true,
      lookupType: 'revrange',
      supportsNegation: true,
    },
    'w.metrics.impressions': {
      type: 'range',
      description: 'A range of pixel impressions to filter company list',
      required: false,
      requiresLookup: false,
    },
    'w.metrics.visits': {
      type: 'range',
      description: 'A range of website visits to filter company list',
      required: false,
      requiresLookup: false,
    },
    'w.metrics.customImpactScore': {
      type: 'range',
      description: 'A range of custom impact scores to filter company list',
      required: false,
      requiresLookup: false,
    },
    'w.inTam': {
      type: 'boolean',
      description:
        'True to see companies which belong to the TAM audience (Total Addressable Market) and false to see companies which do not belong to TAM',
      required: false,
      requiresLookup: false,
    },
    'w.isEngaged': {
      type: 'boolean',
      description:
        'To only be used with a pixelIds filter. Indicates whether a company took an action associated with the pixel and landed on the website.',
      required: false,
      requiresLookup: false,
    },
    'w.webActivityPagesList': {
      type: 'ruleBased',
      description: 'Website page fragments visited by the company to filter company list',
      required: false,
      requiresLookup: false,
      supportsNegation: false,
    },
    'w.sortBy': {
      type: 'enum',
      enumValues: [
        'name',
        'region.id',
        'sector.id',
        'revRange.id',
        'metrics.impressions',
        'metrics.visits',
        'metrics.customImpactScore',
        'metrics.lastActivityDate',
      ],
      description:
        'The field to sort the company list by. Only use this if sorting is requested by the user.',
      required: false,
      requiresLookup: false,
    } satisfies RouteSearchParamEnumType<WEB_TRAFFIC_ACTIVITY_SORT_KEYS>,
    'w.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      description:
        'The sort order. Fields with type `number` or `date` are usually best sorted in descending order. While string fields are best sorted in ascending order.',
      required: false,
      requiresLookup: false,
    },
    'w.page': {
      type: 'string',
      description: 'The page number to display in the company list',
      required: false,
      requiresLookup: false,
    },
    'w.size': {
      type: 'string',
      description: 'The number of items to display per page in the company list',
      required: false,
      requiresLookup: false,
    },
  },
} satisfies NavigableRouteType<ALL_WEB_TRAFFIC_SEARCH_PARAM_KEYS>;

export const companyDetail = {
  path: `${instance.path}/web-traffic-activity/:company`,
  description:
    'Shows details about a company which may have been marketed to or could be in the future.',
  navigable: true,
  pathParams: {
    company: {
      type: 'string',
      description: 'The ID of the company',
      required: true,
      requiresLookup: true,
      lookupType: 'company',
    },
  },
  searchParams: {},
} satisfies NavigableRouteType;

/**
 * Returns a URL string for the web traffic activity route with the given search params.
 *
 * @param params An object with keys that match the search params for the web traffic activity
 * route. Undefined values are skipped.
 * @returns A URL string with the search params appended as query params
 */
export const webTrafficActivityWithParams = (params: Partial<WebTrafficSearchParamType>) => {
  return getPathWithParams(webTrafficActivity.path, params);
};

export const companyById = (companyId?: string) =>
  companyId && `${webTrafficActivity.path}/${encodeURIComponent(companyId)}`;
